import { InputBaseType } from '@/components/ReportForm/InputBase/types';
import { useTranslation } from '@/i18n';
import {
  FormInput,
  FormInputProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { usePostalCodeFormat } from './hooks/usePostalCodeFormat';

export type Props<TFieldValues extends FieldValues> = Omit<
  InputBaseType<TFieldValues>,
  'label' | 'tooltip' | 'caption' | 'hidden'
> &
  FormInputProps<TFieldValues>;

const InnerContactAddressPostalCode = <TFieldValues extends FieldValues>({
  control,
  name,
  inputId,
  required = false,
  ...rest
}: Props<TFieldValues>) => {
  const { t } = useTranslation();
  const { field } = useController({ control, name });
  const { handleChange } = usePostalCodeFormat(field.onChange);

  const rules = useMemo(
    () => ({
      required: required ? t('required') : false,
      validate: {
        format: (value: string | undefined) => {
          if (!value) return true;
          // Remove hyphen and check if exactly 7 digits
          const digitsOnly = value.replace(/-/g, '');
          if (!/^\d{7}$/.test(digitsOnly)) {
            return t('ap_payee_postal_code_format_error');
          }
          return true;
        },
      },
    }),
    [required, t]
  );

  const labelProps = useMemo(
    () => ({
      text: t('ap_payee_postal_code'),
    }),
    [t]
  );

  const inputProps = useMemo(
    () => ({
      label: t('ap_payee_postal_code'),
      placeholder: t('ap_payee_postal_code_placeholder'),
      onChange: handleChange,
      maxLength: 8, // XXX-XXXX format
      ...rest.inputProps,
    }),
    [handleChange, rest.inputProps, t]
  );

  return (
    <FormInput<TFieldValues>
      label={labelProps}
      control={control}
      name={name}
      inputId={inputId}
      rules={rules}
      inputProps={inputProps}
      {...rest}
    />
  );
};

InnerContactAddressPostalCode.displayName = 'InnerContactAddressPostalCode';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ContactAddressPostalCode = memo((props) => (
  <InnerContactAddressPostalCode {...props} />
)) as typeof InnerContactAddressPostalCode;
ContactAddressPostalCode.displayName = 'ContactAddressPostalCode';
